/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

/* eslint-env browser */

import React, { useEffect, useState, useRef } from 'react';
import { Box, Flex, VStack, Separator } from 'chakra-v3';
import type { TaskRecord } from '@karya/core';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Text } from './Text';
import { models } from '../service/db/Setup';

/**
 * Props for the ProjectCard component
 * @property project - The project record containing project details
 * @property tasks - Array of task records associated with the project
 * @property index - Zero-based index for display numbering
 * @property designSrc - URL/path to the project's design asset image
 * @property href - Navigation link for the card
 */
export interface TaskCardProps {
  task: TaskRecord;
  index: number;
  designSrc: string;
  href: string;
}

export interface AssignmentProgressInfo {
  totalAssigned: number;
  completed: number;
}

/**
 * ProjectCard component for displaying individual project information
 *
 * Displays a project card with:
 * - Project icon placeholder (geometric pattern)
 * - Project number with vertical divider
 * - Project name
 * - Project status
 *
 * @example
 * ```tsx
 * <ProjectCard
 *   project={projectRecord}
 *   tasks={taskList}
 *   index={0}
 *   designSrc="/assets/project.svg"
 * />
 * ```
 */
const CARD_WIDTH = 250;
const DESIGN_PREVIEW_SIZE = 220;

/**
 * Reusable SVG component that loads SVG content dynamically and replaces colors
 * This allows any SVG to be used without hardcoding each one
 */
interface DynamicSVGProps {
  src: string;
  strokeColor: string;
  fillColor?: string;
}

// Module-level cache for modified SVGs keyed by `${src}-${strokeColor}-${fillColor||'none'}`
const svgCache = new Map<string, string>();

const FETCH_TIMEOUT_MS = 5000;

const DynamicSVG: React.FC<DynamicSVGProps> = ({ src, strokeColor, fillColor }) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Create cache key
    const cacheKey = `${src}-${strokeColor}-${fillColor || 'none'}`;

    // Check cache first
    const cachedSvg = svgCache.get(cacheKey);
    if (cachedSvg) {
      setSvgContent(cachedSvg);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    // Reset states for new fetch
    setIsLoading(true);
    setHasError(false);
    setSvgContent('');

    // Create new AbortController for this fetch
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Set up timeout
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, FETCH_TIMEOUT_MS);

    // Fetch the SVG file as text
    fetch(src, { signal: abortController.signal })
      .then((response) => {
        // Handle non-OK HTTP responses as errors
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        // Clear timeout on success
        clearTimeout(timeoutId);

        // Replace all stroke colors with the desired color
        // This regex matches stroke="#..." or stroke='...' patterns
        let modifiedSvg = text.replace(/stroke="[^"]*"/g, `stroke="${strokeColor}"`);
        modifiedSvg = modifiedSvg.replace(/stroke='[^']*'/g, `stroke='${strokeColor}'`);

        // Replace fill colors if provided
        if (fillColor) {
          modifiedSvg = modifiedSvg.replace(/fill="[^"]*"/g, `fill="${fillColor}"`);
          modifiedSvg = modifiedSvg.replace(/fill='[^']*'/g, `fill='${fillColor}'`);
        }

        // Sanitize the modified SVG with DOMPurify
        const sanitizedSvg = DOMPurify.sanitize(modifiedSvg, {
          USE_PROFILES: { svg: true },
        });

        // Cache the sanitized SVG
        svgCache.set(cacheKey, sanitizedSvg);

        // Update state only if not aborted
        if (!abortController.signal.aborted) {
          setSvgContent(sanitizedSvg);
          setIsLoading(false);
          setHasError(false);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        // Only update state if not aborted (abort is expected behavior)
        if (!abortController.signal.aborted) {
          console.error('Failed to load SVG:', err);
          setHasError(true);
          setIsLoading(false);
        }
      });

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [src, strokeColor, fillColor]);

  // Show loading placeholder
  if (isLoading) {
    return <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center" bg="transparent" />;
  }

  // Show error state
  if (hasError) {
    return (
      <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center" bg="transparent">
        <Text variant="caption" color="text.grey20">
          Icon unavailable
        </Text>
      </Box>
    );
  }

  // Render the sanitized SVG content
  return <Box dangerouslySetInnerHTML={{ __html: svgContent }} w="100%" h="100%" />;
};

export const computeAssignmentProgressInfo = async (task_id: string): Promise<AssignmentProgressInfo> => {
  const assignments = await models.basic
    .getAllFromIndex('microtask_assignment', 'task_id', task_id)
    .unwrapResult()
    .getOr([]);

  const { completed, totalAssigned } = assignments.reduce(
    (acc, assignment) => {
      if (assignment.status === 'COMPLETED') {
        acc.completed += 1;
      }
      if (assignment.status === 'COMPLETED' || assignment.status === 'ASSIGNED') {
        acc.totalAssigned += 1;
      }
      return acc;
    },
    { completed: 0, totalAssigned: 0 },
  );

  return { completed, totalAssigned };
};

export const TaskCard: React.FC<TaskCardProps> = ({ task, index, designSrc, href }) => {
  const [completionState, setCompletionState] = useState<AssignmentProgressInfo | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    computeAssignmentProgressInfo(task.id)
      .then(setCompletionState)
      .catch((err) => {
        console.error('Failed to compute progress for task. Error: ', err);
      });
  }, [task.id]);

  // Determine stroke color based on hover state
  // using semantic tokens messes up default state and IDK why so kept the hex values
  const strokeColor = isHovered ? '#90E7FF' : '#00AFDF';

  return (
    <Link to={href} style={{ textDecoration: 'none', width: '100%', maxWidth: CARD_WIDTH, display: 'block' }}>
      <Box
        w="100%"
        maxW={`${CARD_WIDTH}px`}
        borderRadius="4px"
        p="1px"
        transition="all 0.2s ease-in-out"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        _hover={{
          backgroundImage:
            'radial-gradient(99.74% 65.32% at 48.71% 100%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)',
          boxShadow: '0 0 20px rgba(144, 231, 255, 0.4), 0 0 40px rgba(144, 231, 255, 0.2)',
          color: 'text.primary',
          borderColor: 'border.white20',
        }}
        _focusWithin={{ boxShadow: 'outline' }}
      >
        <VStack gap="8px" alignItems="flex-start" p="24px 16px" w="100%" borderRadius="4px" bg="bg.card">
          {/* Project Icon Placeholder */}
          <Box w="100%" display="flex" justifyContent="flex-start">
            <Flex
              w={`${DESIGN_PREVIEW_SIZE}px`}
              h={`${DESIGN_PREVIEW_SIZE}px`}
              borderRadius="6px"
              alignItems="center"
              justifyContent="center"
              bg="transparent"
              flexShrink={0}
              overflow="hidden"
            >
              <Box
                w="100%"
                h="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                transition="all 0.2s ease-in-out"
              >
                <DynamicSVG src={designSrc} strokeColor={strokeColor} />
              </Box>
            </Flex>
          </Box>

          {/* Project Info */}
          <VStack gap="10px" alignItems="flex-start" w="100%">
            {/* Project Number and Name */}
            <VStack gap="8px" alignItems="flex-start" w="100%">
              {/* Project Number with Divider */}
              <Box position="relative">
                <Text variant="projectIndex">{String(index + 1).padStart(2, '0')}</Text>
                {/* Horizontal divider line - positioned to the right of the number */}
                <Separator orientation="horizontal" h="22px" color="text.grey20" opacity={0.7} left="full" />
              </Box>
              <Text variant="projectTitle">{task.name}</Text>
            </VStack>

            {/* Project Status */}
            {completionState && completionState.totalAssigned > 0 ? (
              <Text variant="projectStatus">
                {completionState.completed} completed of {completionState.totalAssigned}
              </Text>
            ) : (
              <Text variant="projectStatus">Progress Unavailable</Text>
            )}
          </VStack>
        </VStack>
      </Box>
    </Link>
  );
};
